import type { CSSProperties, ReactNode } from 'react'
import { HandUnderline } from '@/components/brand/HandUnderline'
import { GlassPanel } from '@/components/sections/GlassPanel'
import { communityCopy, fillCopy } from '@/nominations/communityCopy'
import { NomineeCard } from '@/nominations/components/NomineeCard'
import type { NominationConfig } from '@/nominations/types'

type NominationHeroProps = {
  nomination: NominationConfig
  titleId: string
}

/** Splits `{mark}...{/mark}` out of the title so the marked phrase gets the hand-drawn underline. */
const renderMarkedTitle = (template: string): ReactNode[] =>
  template.split(/(\{mark\}.*?\{\/mark\})/).map((part, index) => {
    const marked = part.match(/^\{mark\}(.*?)\{\/mark\}$/)
    return marked ? <HandUnderline key={index}>{marked[1]}</HandUnderline> : part
  })

const delay = (ms: number) => ({ '--delay': `${ms}ms` }) as CSSProperties

/* The glass stays still until the pointer stirs it: these pages have no pause control. */
export const NominationHero = ({ nomination, titleId }: NominationHeroProps) => {
  const title = fillCopy(communityCopy.heroTitle, {
    nomineeFirst: nomination.nominee.firstName,
    nominatorFirst: nomination.nominator.firstName,
  })

  return (
    <GlassPanel className="hero nom-hero" shade="hero" labelledBy={titleId} ambient={false}>
      <div className="nav-space" aria-hidden="true" />
      <div className="hero-body">
        <div className="hero-copy nom-reveal" style={delay(80)}>
          <p className="x-eyebrow x-eyebrow--ink">{communityCopy.heroEyebrow}</p>
          <h1 id={titleId} className="nom-title">
            {renderMarkedTitle(title)}
          </h1>
          <p className="hero-lead">{communityCopy.heroLead}</p>
        </div>
        <div className="nom-hero-card nom-reveal" style={delay(220)}>
          <NomineeCard nomination={nomination} />
        </div>
      </div>
    </GlassPanel>
  )
}
